import { CurrencyCode } from '@/shared/constants/currencies';
import {
  EcommerceItem,
  EcommercePayloadCart,
  EcommercePayloadItem,
} from '@/features/gtm/internal/types/ecommerce-payload-item';
import { CartData } from '@/features/cart/shared/types/cart.types';
import { cartItemToEcommerceParams } from '@/features/cart/shared/helpers/cart-item-to-ecommerce-params';

type SKU = string;

export interface CreateEcommercePayloadCartParams {
  currency: CurrencyCode;
  value: number;
  items: CreateEcommercePayloadItemParams[];
}

export interface CreateEcommercePayloadItemParams {
  currency: CurrencyCode;
  itemId: SKU;
  itemName: string;
  variant: 'purchase' | 'rental';
  price: number;
  originalPrice?: number;
  discount?: number;
  discountReason?: 'sale' | 'membership' | 'promotion';
  itemBrand?: string;
  productType?: string;
  categories?: string[];
  quantity?: number;
}

function createEcommerceItem(params: CreateEcommercePayloadItemParams): EcommerceItem {
  const {
    currency,
    itemId,
    itemName,
    variant,
    price,
    originalPrice,
    discount,
    discountReason,
    itemBrand,
    productType,
    categories = [],
    quantity = 1,
  } = params;

  const item: EcommerceItem = {
    item_id: itemId,
    item_name: itemName,
    price,
    currency,
    quantity,
  };

  if (itemBrand) item.item_brand = itemBrand;
  if (productType) item.product_type = productType;

  if (variant) item.item_variant = variant;

  // Discounts
  if (discount) {
    item.discount = discount;
    item.discount_amount = discount; // GA4 parity
  }
  if (discountReason) item.discount_reason = discountReason;

  if (originalPrice) item.original_price = originalPrice;

  // Categories
  categories.forEach((cat, index) => {
    if (!cat) return;
    const key = index === 0 ? 'item_category' : `item_category${index + 1}`;
    item[key] = cat;
  });

  return item;
}

export function createEcommercePayloadForCart(
  cart: CartData,
  isWildMember: boolean = false,
): EcommercePayloadCart {
  const eCommerceItems: EcommerceItem[] = [];
  cart.items.forEach((item) => {
    const ecommerceItemData = cartItemToEcommerceParams(item, isWildMember);
    eCommerceItems.push(createEcommerceItem(ecommerceItemData));
  });

  return {
    ecommerce: {
      value: cart.totalPrice.value,
      currency: cart.totalPrice.currencyCode,
      items: eCommerceItems,
    },
  };
}

export function createEcommercePayload(
  params: CreateEcommercePayloadItemParams,
): EcommercePayloadItem {
  const item = createEcommerceItem(params);
  const finalValue = item.price * item.quantity;

  return {
    ecommerce: {
      value: finalValue,
      currency: params.currency,
      items: [item],
    },
  };
}
